import type { ReactNode } from 'react';
import { fmtMoney } from '../utils/format';

export type TimelineEventType =
  | 'now'
  | 'snapshot'
  | 'purchase'
  | 'retirement'
  | 'payoff'
  | 'birthday';

export interface TimelineEvent {
  id: string;
  /** Sort key — ms since epoch (snapshots use date; year-only events use Jan 1 of that year). */
  sortMs: number;
  type: TimelineEventType;
  /** Short date/year label shown above the title. */
  whenLabel: string;
  title: string;
  subtitle?: string;
  amount?: number;
  amountTone?: 'pos' | 'neg' | 'neutral';
  onClick?: () => void;
  past: boolean;
}

interface Props {
  events: TimelineEvent[];
  title?: string;
  right?: ReactNode;
  maxEvents?: number;
}

const ICON_BG: Record<TimelineEventType, string> = {
  now: 'bg-accent-600 text-white',
  snapshot: 'bg-white text-ink-600 dark:bg-ink-800 dark:text-ink-100',
  purchase: 'bg-neg-600 text-white',
  retirement: 'bg-accent-500 text-white',
  payoff: 'bg-pos-600 text-white',
  birthday: 'bg-ink-600 text-white',
};

export function Timeline({ events, title = 'Timeline', right, maxEvents }: Props) {
  const sorted = [...events].sort((a, b) => a.sortMs - b.sortMs);
  const visible = maxEvents ? sorted.slice(0, maxEvents) : sorted;
  if (visible.length === 0) return null;

  return (
    <section className="card card-dark flex h-full flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between border-b border-ink-100 px-4 py-2 dark:border-ink-700">
        <div className="label">{title}</div>
        {right}
      </div>
      <ol className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">
        {/* The rail */}
        <div className="pointer-events-none absolute left-[27px] top-3 bottom-3 w-[2px] bg-gradient-to-b from-ink-100 via-ink-200 to-ink-100 dark:from-ink-700 dark:via-ink-600 dark:to-ink-700" />
        {visible.map((e, i) => (
          <li
            key={e.id}
            className={`relative flex gap-3 ${i === visible.length - 1 ? '' : 'pb-4'}`}
          >
            <div
              className={`relative z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ring-2 ring-ink-50 dark:ring-ink-800 ${ICON_BG[e.type]}`}
              aria-hidden
            >
              <EventIcon type={e.type} />
            </div>
            <button
              type="button"
              onClick={e.onClick}
              disabled={!e.onClick}
              className={`group flex-1 min-w-0 rounded-lg px-2 py-1 text-left transition ${
                e.onClick ? 'hover:bg-ink-50 dark:hover:bg-ink-700/60' : 'cursor-default'
              } ${e.past ? 'opacity-60' : ''}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-[10px] font-mono font-semibold uppercase tracking-wider tabular-nums text-ink-500 dark:text-ink-300">
                  {e.whenLabel}
                </div>
                {e.amount != null && (
                  <div
                    className={`shrink-0 font-mono text-xs font-semibold tabular-nums ${
                      e.amountTone === 'pos'
                        ? 'text-pos-700 dark:text-pos-500'
                        : e.amountTone === 'neg'
                          ? 'text-neg-700 dark:text-neg-500'
                          : 'text-ink-700 dark:text-ink-100'
                    }`}
                  >
                    {e.amountTone === 'neg' && e.amount > 0
                      ? `−${fmtMoney(e.amount)}`
                      : e.amountTone === 'pos' && e.amount > 0
                        ? `+${fmtMoney(e.amount)}`
                        : fmtMoney(e.amount)}
                  </div>
                )}
              </div>
              <div className="truncate text-sm font-semibold text-ink-900 dark:text-ink-50">
                {e.title}
              </div>
              {e.subtitle && (
                <div className="truncate text-[11px] text-ink-500 dark:text-ink-300">
                  {e.subtitle}
                </div>
              )}
            </button>
          </li>
        ))}
        {maxEvents && sorted.length > visible.length && (
          <li className="mt-1 pl-9 text-[11px] text-ink-400 dark:text-ink-500">
            +{sorted.length - visible.length} more…
          </li>
        )}
      </ol>
    </section>
  );
}

function EventIcon({ type }: { type: TimelineEventType }) {
  const common = {
    width: 12,
    height: 12,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (type) {
    case 'now':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'purchase':
      return (
        <svg {...common}>
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      );
    case 'retirement':
      return (
        <svg {...common}>
          <path d="M4 22V4l14 6-14 6" />
        </svg>
      );
    case 'payoff':
      return (
        <svg {...common}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'birthday':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4" />
        </svg>
      );
    case 'snapshot':
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />
        </svg>
      );
  }
}
